import { useEffect, useMemo, useState } from 'react'
import { useVeil } from '../lib/useVeil'
import { maskName } from '../lib/security'
import { api } from '../lib/api'
import { setSageContext, clearSageContext } from '../lib/sagebus'
import type { FamilyEvent, Tree } from '../lib/types'

const KIND_ICON: Record<string, string> = {
  meetup: '🫂', reunion: '🎉', memorial: '🕯️', birthday: '🎂', other: '📌',
}
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DOW = ['Su','Mo','Tu','We','Th','Fr','Sa']

interface DayCell {
  date: string  // YYYY-MM-DD
  day: number
  inMonth: boolean
}

interface BirthdayMark { name: string; kind: 'birthday' | 'memorial'; tree: string }

export default function Calendar() {
  const veil = useVeil()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth())  // 0-based
  const [events, setEvents] = useState<FamilyEvent[]>([])
  const [marks, setMarks] = useState<Map<string, BirthdayMark[]>>(new Map())
  const [trees, setTrees] = useState<Tree[]>([])
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ tree_id: '', title: '', location: '', kind: 'meetup', date: '', time: '12:00', description: '' })
  const [busy, setBusy] = useState(false)

  const from = `${year}-${String(month + 1).padStart(2, '0')}-01`
  const to = `${year}-${String(month + 1).padStart(2, '0')}-31T23:59`

  const load = () => {
    api.listEvents(from, to).then(({ events: evs, persons }) => {
      setEvents(evs)
      // birthdays of the living + memorial anniversaries, matched to this month by MM-DD
      const m = new Map<string, BirthdayMark[]>()
      const mm = String(month + 1).padStart(2, '0')
      for (const p of persons) {
        const name = maskName(p, veil)
        if (p.birth_date && p.is_living && p.birth_date.slice(5, 7) === mm) {
          const key = `${year}-${p.birth_date.slice(5, 10)}`
          m.set(key, [...(m.get(key) || []), { name, kind: 'birthday', tree: p.tree_name }])
        }
        if (p.death_date && p.death_date.slice(5, 7) === mm) {
          const key = `${year}-${p.death_date.slice(5, 10)}`
          m.set(key, [...(m.get(key) || []), { name, kind: 'memorial', tree: p.tree_name }])
        }
      }
      setMarks(m)
    }).catch(() => {})
  }

  useEffect(() => { load() }, [year, month, veil]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.listTrees().then(setTrees).catch(() => {}) }, [])

  // Sage context: agent can plan meet-ups from the calendar page too
  useEffect(() => {
    const editable = trees.find(t => t.my_role !== 'viewer')
    if (editable) {
      setSageContext({ treeId: editable.id, treeName: `Calendar · ${editable.name}`, refresh: () => load() })
    }
    return () => clearSageContext()
  }, [trees]) // eslint-disable-line react-hooks/exhaustive-deps

  const cells = useMemo<DayCell[]>(() => {
    const first = new Date(year, month, 1)
    const start = first.getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysPrev = new Date(year, month, 0).getDate()
    const out: DayCell[] = []
    for (let i = start - 1; i >= 0; i--) {
      const d = daysPrev - i
      const mm = month === 0 ? 12 : month
      const yy = month === 0 ? year - 1 : year
      out.push({ date: `${yy}-${String(mm).padStart(2, '0')}-${String(d).padStart(2, '0')}`, day: d, inMonth: false })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      out.push({ date: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, day: d, inMonth: true })
    }
    while (out.length % 7 !== 0) {
      const idx = out.length - (start + daysInMonth) + 1
      const mm = month === 11 ? 1 : month + 2
      const yy = month === 11 ? year + 1 : year
      out.push({ date: `${yy}-${String(mm).padStart(2, '0')}-${String(idx).padStart(2, '0')}`, day: idx, inMonth: false })
    }
    return out
  }, [year, month])

  const eventsByDay = useMemo(() => {
    const m = new Map<string, FamilyEvent[]>()
    for (const e of events) {
      const key = e.start_at.slice(0, 10)
      m.set(key, [...(m.get(key) || []), e])
    }
    return m
  }, [events])

  const todayStr = new Date().toISOString().slice(0, 10)

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1) } else setMonth(m => m - 1) }
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1) } else setMonth(m => m + 1) }

  const openCreate = (date?: string) => {
    setForm(f => ({ ...f, date: date || todayStr, tree_id: f.tree_id || trees.find(t => t.my_role !== 'viewer')?.id || '' }))
    setShowCreate(true)
  }

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await api.createEvent({
        tree_id: form.tree_id, title: form.title.trim(), location: form.location,
        kind: form.kind, description: form.description,
        start_at: `${form.date}T${form.time}`,
      })
      setShowCreate(false)
      setForm(f => ({ ...f, title: '', location: '', description: '' }))
      load()
    } finally { setBusy(false) }
  }

  const doRsvp = async (ev: FamilyEvent, status: 'going' | 'maybe' | 'no') => {
    await api.rsvp(ev.id, status)
    load()
  }

  const dayDetail = selectedDay ? {
    events: eventsByDay.get(selectedDay) || [],
    marks: marks.get(selectedDay) || [],
  } : null

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100">Family Meet-Up Calendar</h1>
          <p className="text-xs text-slate-500 mt-1">Reunions, birthdays of the living, and days of remembrance — all constellations combined.</p>
        </div>
        <div className="flex gap-2">
          <a href={api.icalUrl()} download="family-ancestry.ics" className="btn-ghost !py-2 text-sm" title="Import into Google/Apple/Outlook calendars">
            📥 iCal
          </a>
          <button onClick={() => openCreate()} className="btn-gold !py-2 text-sm">+ Plan a Meet-Up</button>
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="btn-ghost !px-3 !py-1.5 text-sm">←</button>
        <h2 className="font-display text-xl font-semibold text-gradient-gold">{MONTHS[month]} {year}</h2>
        <button onClick={nextMonth} className="btn-ghost !px-3 !py-1.5 text-sm">→</button>
      </div>

      {/* Grid */}
      <div className="card-glass p-2 sm:p-3">
        <div className="grid grid-cols-7 mb-1">
          {DOW.map(d => <div key={d} className="text-center text-[10px] uppercase tracking-wider text-slate-600 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map(c => {
            const evs = eventsByDay.get(c.date) || []
            const mks = marks.get(c.date) || []
            const isToday = c.date === todayStr
            return (
              <button key={c.date} onClick={() => setSelectedDay(c.date)}
                className={`min-h-[62px] sm:min-h-[80px] rounded-lg p-1 sm:p-1.5 text-left border transition-colors ${
                  isToday ? 'border-gold-500/60 bg-gold-500/[0.07]'
                    : selectedDay === c.date ? 'border-rootv-500/50 bg-rootv-500/10'
                    : 'border-white/[0.04] hover:border-white/15'
                } ${c.inMonth ? '' : 'opacity-30'}`}>
                <span className={`text-[11px] ${isToday ? 'text-gold-300 font-bold' : 'text-slate-400'}`}>{c.day}</span>
                <div className="mt-0.5 space-y-0.5">
                  {evs.slice(0, 2).map(e => (
                    <div key={e.id} className="text-[9px] sm:text-[10px] truncate px-1 py-px rounded bg-gold-500/15 text-gold-200">
                      {KIND_ICON[e.kind]} {e.title}
                    </div>
                  ))}
                  {mks.slice(0, 2 - Math.min(evs.length, 2)).map((mk, i) => (
                    <div key={i} className={`text-[9px] sm:text-[10px] truncate px-1 py-px rounded ${mk.kind === 'birthday' ? 'bg-rootv-500/15 text-rootv-400' : 'bg-white/5 text-slate-400'}`}>
                      {mk.kind === 'birthday' ? '🎂' : '🕯️'} {mk.name}
                    </div>
                  ))}
                  {(evs.length + mks.length) > 2 && <div className="text-[9px] text-slate-500 px-1">+{evs.length + mks.length - 2} more</div>}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Day detail */}
      {dayDetail && (dayDetail.events.length > 0 || dayDetail.marks.length > 0) && (
        <div className="card-glass p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-slate-100">{selectedDay}</h3>
            <button onClick={() => openCreate(selectedDay!)} className="text-xs text-gold-400 hover:text-gold-300">+ add meet-up this day</button>
          </div>
          <div className="space-y-2.5">
            {dayDetail.events.map(e => (
              <div key={e.id} className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100 font-medium">{KIND_ICON[e.kind]} {e.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {e.start_at.slice(11, 16)}{e.location ? ` · ${e.location}` : ''} · {e.tree_name} · {e.going_count || 0} going
                    </p>
                    {e.description && <p className="text-xs text-slate-400 mt-1">{e.description}</p>}
                  </div>
                  <button onClick={() => api.deleteEvent(e.id).then(load).catch(() => {})}
                    className="text-slate-600 hover:text-red-400 text-xs flex-shrink-0">✕</button>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {(['going', 'maybe', 'no'] as const).map(s => (
                    <button key={s} onClick={() => doRsvp(e, s)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                        e.my_rsvp === s ? 'bg-gold-500/20 border-gold-500/50 text-gold-200' : 'border-white/10 text-slate-400 hover:border-white/25'
                      }`}>
                      {s === 'going' ? '✓ Going' : s === 'maybe' ? '? Maybe' : '✗ Can\'t'}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {dayDetail.marks.map((mk, i) => (
              <div key={i} className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3">
                <p className="text-sm text-slate-200">{mk.kind === 'birthday' ? '🎂' : '🕯️'} {mk.name}</p>
                <p className="text-xs text-slate-500">{mk.kind === 'birthday' ? 'Birthday' : 'Remembrance'} · {mk.tree}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-void-950/85 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowCreate(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="card-glass !bg-void-900/95 p-6 w-full max-w-md agent-enter">
            <h2 className="font-display text-lg font-bold text-slate-100 mb-4">Plan a family meet-up</h2>
            <input required autoFocus placeholder="Title — e.g. Summer BBQ at the lake" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="input-dark mb-3" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input type="date" required value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="input-dark" />
              <input type="time" value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="input-dark" />
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className="input-dark">
                <option value="meetup">🫂 Meet-up</option>
                <option value="reunion">🎉 Reunion</option>
                <option value="memorial">🕯️ Memorial</option>
                <option value="birthday">🎂 Birthday</option>
                <option value="other">📌 Other</option>
              </select>
              <select required value={form.tree_id} onChange={e => setForm(f => ({ ...f, tree_id: e.target.value }))} className="input-dark">
                <option value="">Which family?</option>
                {trees.filter(t => t.my_role !== 'viewer').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <input placeholder="Location" value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="input-dark mb-3" />
            <textarea placeholder="Details (optional)" rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input-dark mb-5 resize-none" />
            <div className="flex gap-3">
              <button type="submit" disabled={busy || !form.title.trim() || !form.tree_id || !form.date} className="btn-gold flex-1">
                {busy ? 'Planning…' : 'Send the ravens 🕊️'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
